import { Card, Empty, Select } from 'antd'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import ActionsLayout from '@/components/MatchLayout'
import { useMatch } from '@/hooks/swr/useMatch'
import { useTeams } from '@/hooks/swr/useTeam'
import WechatShare from '@/components/WechatShare'
import TurnSetter from '@/components/TurnSetter'
import { scoreToColumn } from '@/utils/utils'
import { GetServerSideProps } from 'next/types'
import Button from '@/components/Button'
import { useChannel } from '@/hooks/useWebSocket'
import useRooms from '@/hooks/swr/useRoom'
import { sumBy, sortBy, orderBy, findIndex } from 'lodash-es'
import produce from 'immer'
import ColumnResizeTable from '@/components/ColumnResizeTable'
import { ColumnsType } from 'antd/es/table'
import { useTurnDate } from './timer'

type PropType = { match_id: string }

type turnType = number | string

const TeamsScore: Next.PageWithLayout<PropType> = ({ match_id, wsTurn, showWinner = false, showTurnSetter = true, wsGenderFilter = '全部' }) => {
  const [turn, setTurn] = useState<turnType>('sum')
  const [genderFilter, setGenderFilter] = useState('全部')

  useEffect(() => {
    setGenderFilter(wsGenderFilter)
  }, [wsGenderFilter])

  useEffect(() => {
    if (wsTurn) {
      setTurn(wsTurn)
    }
  }, [wsTurn])

  const { data: match, mutate: mutateMatch } = useMatch(match_id)
  const { data: rooms } = useRooms(match_id, turn, match.is_encrypted)
  const { data: teams, mutate: mutateTeams } = useTeams(match_id, turn, match.is_encrypted)
  const { data: tempTeams } = useTeams(match_id, 'temp', match.is_encrypted)
  const { turn: rtrbTurn } = useTurnDate(match)

  useChannel(`match-${match_id}`, true, (obj) => {
    switch (obj.action) {
      case 'mutateRounds':
      case 'mutateTeams':
      case 'updateRound':
        mutateTeams()
        break
      // 排名依据(顺序)保存后:重拉比赛设置(表头列)与排名
      case 'mutateMatch':
        mutateMatch()
        mutateTeams()
        break
      case 'updateRTRB':
      case 'unSubmit':
      case 'submitRound': {
        const team1 = obj.data.team1
        const team2 = obj.data.team2
        if (team1 && team2) {
          mutateTeams(
            (cachedTeams) =>
              produce(cachedTeams, (draft) => {
                const team1Index = findIndex(cachedTeams, (t) => t.id === team1.id)
                if (team1Index >= 0) draft[team1Index] = team1
                const team2Index = findIndex(cachedTeams, (t) => t.id === team2.id)
                if (team2Index >= 0) draft[team2Index] = team2
              }),
            false
          )
        }
        break
      }
      default:
        break
    }
  })

  const scores = useMemo(() => {
    if (turn === 'sum') {
      return (match.sort_factors ?? []).map((i) => scoreToColumn(i, match?.match_type))
    }
    if (match.match_type === 'duplicated_switzer') {
      if (match.sort_factors.includes('offset_median_score') || match.sort_factors.includes('percent_score')) {
        return [...match.sort_factors, 'score'].map((i) => scoreToColumn(i, match?.match_type))
      }
      return ['offset_victory_score', 'offset_score', 'score'].map((i) => scoreToColumn(i, match?.match_type))
    }
    return ['victory_score', 'score', 'rank_up_score'].map((i) => scoreToColumn(i, match?.match_type))
  }, [match.match_type, match.sort_factors, turn])

  const wechatShare = useMemo(() => {
    if (match.name) {
      return <WechatShare res={{ title: match.name }} />
    }
    return null
  }, [match.name])

  const realTimeTeams = useMemo(() => {
    if (!teams || !tempTeams) return []

    return teams.map((team) => {
      const result = { ...team }
      const tempTeam = tempTeams.find((t) => t.id === team.id)

      if (tempTeam) {
        match?.sort_factors?.forEach((factor) => {
          if (factor !== 'score_log') {
            result[`temp_${factor}`] = tempTeam[`temp_${factor}`] || 0
            result[`total_${factor}`] = (team[factor] || 0) + (tempTeam[`temp_${factor}`] || 0)
          } else {
            result[`temp_${factor}`] = tempTeam[`temp_${factor}`] || []
            result[`total_${factor}`] = [...(team[factor] || []), ...(tempTeam[`temp_${factor}`] || [])]
          }
        })
      }

      return result
    })
  }, [match?.sort_factors, teams, tempTeams])

  const tableTeams = useMemo(() => {
    let rankableTeams = teams.filter((t) => t.rankable !== false)
    if (match.group_by_gender) {
      rankableTeams = rankableTeams.filter((t) => t.gender === genderFilter || genderFilter === '全部')
    }
    if (!match?.id) {
      return rankableTeams
    }
    // 匹克球小组淘汰赛：淘汰阶段按名次附加赛决出的最终名次(1-8)排序展示
    if (match.match_type === 'pickleball_group_elimination' && (match.pickleball_phase === 'elimination' || match.pickleball_phase === 'finished')) {
      const ranked = rankableTeams.filter((t) => t.pickleball_final_rank != null)
      const rest = rankableTeams.filter((t) => t.pickleball_final_rank == null)
      if (ranked.length > 0) {
        const sortedRanked = sortBy(ranked, (t) => t.pickleball_final_rank)
        const sortedRest = sortBy(rest, (t) => -(t.victory_count || 0))
        return [...sortedRanked, ...sortedRest].map((team, index) => {
          const t = { ...team }
          t.rank_index = t.pickleball_final_rank != null ? t.pickleball_final_rank : index + 1
          return t
        })
      }
    }
    if (match.match_type === 'duplicated_switzer' && turn !== 'sum') {
      const noGiveupRooms = rooms.filter((r) => r.status !== 10 && r.team1_rank !== -10 && r.team2_rank !== -10 && r.temp_team1_rank !== -10 && r.temp_team2_rank !== -10)
      const roomLength = noGiveupRooms.length || 1
      const eastAveScore = +(sumBy(noGiveupRooms, (r) => parseInt(r.team1_rank, 10) || parseInt(r.temp_team1_rank, 10) || 0) / roomLength).toFixed(2)
      const southAveScore = +(sumBy(noGiveupRooms, (r) => parseInt(r.team2_rank, 10) || parseInt(r.temp_team2_rank, 10) || 0) / roomLength).toFixed(2)
      let eastMedianScore = 0
      let northMedianScore = 0
      const eastScoreList = sortBy(noGiveupRooms.map((r) => parseInt(r.team1_rank, 10) || parseInt(r.temp_team1_rank, 10) || 0))
      const northScoreList = sortBy(noGiveupRooms.map((r) => parseInt(r.team2_rank, 10) || parseInt(r.temp_team2_rank, 10) || 0))
      if (roomLength % 2 === 1) {
        eastMedianScore = eastScoreList[Math.floor(roomLength / 2)]
        northMedianScore = northScoreList[Math.floor(roomLength / 2)]
      } else {
        eastMedianScore = (eastScoreList[roomLength / 2] + eastScoreList[roomLength / 2 - 1]) / 2
        northMedianScore = (northScoreList[roomLength / 2] + northScoreList[roomLength / 2 - 1]) / 2
      }
      const eastScoreAt075 = eastScoreList[parseInt((roomLength * 0.75) as any, 10)]
      const eastScoreAt025 = eastScoreList[parseInt((roomLength * 0.25) as any, 10)]
      const northScoreAt075 = northScoreList[parseInt((roomLength * 0.75) as any, 10)]
      const northScoreAt025 = northScoreList[parseInt((roomLength * 0.25) as any, 10)]

      let team1s = []
      let team2s = []
      rooms.forEach((r) => {
        const team1 = teams.find((t) => t.id === r.team1_id)
        if (team1) {
          team1s.push({ team: team1, score: r.team1_rank, index: null })
          team1.direction = 'east_west'
          team1.offset_score = +(team1.score - eastAveScore).toFixed(2)
          team1.offset_victory_score = team1.offset_score >= 0 ? (team1.offset_score === 0 ? 1 : 2) : 0
          team1.offset_median_score = +(team1.score - eastMedianScore).toFixed(2)
          team1.offset_median_victory_score = team1.offset_median_score >= 0 ? (team1.offset_median_score === 0 ? 1 : 2) : 0
          if (team1.score >= eastScoreAt075) {
            team1.offset_median_victory_score_4_stages = 2
          } else if (team1.score >= eastMedianScore) {
            team1.offset_median_victory_score_4_stages = 1.5
          } else if (team1.score >= eastScoreAt025) {
            team1.offset_median_victory_score_4_stages = 1
          } else {
            team1.offset_median_victory_score_4_stages = 0.5
          }
        }
        const team2 = teams.find((t) => t.id === r.team2_id)
        if (team2) {
          team2s.push({ team: team2, score: r.team2_rank, index: null })
          team2.direction = 'north_south'
          team2.offset_score = +(team2.score - southAveScore).toFixed(2)
          team2.offset_victory_score = team2.offset_score >= 0 ? (team2.offset_score === 0 ? 1 : 2) : 0
          team2.offset_median_score = +(team2.score - northMedianScore).toFixed(2)
          team2.offset_median_victory_score = team2.offset_median_score >= 0 ? (team2.offset_median_score === 0 ? 1 : 2) : 0
          if (team2.score >= northScoreAt075) {
            team2.offset_median_victory_score_4_stages = 2
          } else if (team2.score >= northMedianScore) {
            team2.offset_median_victory_score_4_stages = 1.5
          } else if (team2.score >= northScoreAt025) {
            team2.offset_median_victory_score_4_stages = 1.0
          } else {
            team2.offset_median_victory_score_4_stages = 0.5
          }
        }
        if (match.sort_factors.includes('percent_score')) {
          const length = rooms.filter((r) => r.round_id && r.turn === turn).length
          if (length > 0) {
            team1s = sortBy(team1s, (t) => -t.score)
            team2s = sortBy(team2s, (t) => -t.score)
            team1s.forEach((t1, i) => {
              t1.index = i
              if (i !== 0 && t1.score === team1s[i - 1].score) {
                t1.index = team1s[i - 1].index
              }
              const percent = Math.round((100 * (length - t1.index)) / length)
              t1.team.percent_score = percent
            })
            team2s.forEach((t2, i) => {
              t2.index = i
              if (i !== 0 && t2.score === team2s[i - 1].score) {
                t2.index = team2s[i - 1].index
              }
              const percent = Math.round((100 * (length - t2.index)) / length)
              t2.team.percent_score = percent
            })
          }
        }

        if (team1?.name === '轮空') {
          team2.offset_score = +(southAveScore * 0.3).toFixed(2)
          team2.offset_victory_score = 2
        }
        if (team2?.name === '轮空') {
          team1.offset_score = +(eastAveScore * 0.3).toFixed(2)
          team1.offset_victory_score = 2
        }
      })

      rankableTeams.forEach((t) => {
        if (!t.direction) {
          t.percent_score = 0
          t.offset_score = 0
          t.offset_victory_score = 0
          t.offset_median_score = 0
          t.offset_median_victory_score = 0
          t.offset_median_victory_score_4_stages = 0
        }
      })

      rankableTeams = sortBy(rankableTeams, (t) => -t.offset_score)
    }

    let lastTeam: any = {}

    rankableTeams = rankableTeams.map((team, index) => {
      const t = { ...team }
      t.rank_index = index + 1
      let flag = true
      if (index <= 0) {
        flag = false
      } else {
        flag = !match.sort_factors.some((factor) => {
          return `${t[factor]}` !== `${rankableTeams[index - 1][factor]}`
        })
      }
      if (flag) {
        t.rank_index = lastTeam?.rank_index
      }
      lastTeam = t
      return t
    })

    return showWinner ? rankableTeams.slice(0, match.first_prize + match.second_prize + match.third_prize + match.winning_prize) : rankableTeams
  }, [
    teams,
    match.group_by_gender,
    match?.id,
    match.match_type,
    match.first_prize,
    match.second_prize,
    match.third_prize,
    match.winning_prize,
    match.sort_factors,
    turn,
    showWinner,
    genderFilter,
    rooms,
  ])

  const realTimeRanking = useMemo(() => {
    if (!realTimeTeams || realTimeTeams.length === 0) return []

    const sortedTeams = orderBy(
      realTimeTeams,
      match?.sort_factors?.map((factor) => `total_${factor}`),
      [...Array(match?.sort_factors?.length)].map(() => 'desc')
    )

    return sortedTeams
  }, [match.sort_factors, realTimeTeams])

  const calculateRankChange = useCallback(
    (teamId) => {
      if (rtrbTurn < 1.5 || !realTimeRanking || realTimeRanking.length === 0) {
        return null
      }

      const oldIndex = findIndex(tableTeams, (team) => team.id === teamId)
      const newIndex = findIndex(realTimeRanking, (team) => team.id === teamId)

      if (oldIndex === -1 || newIndex === -1) return null

      const change = oldIndex - newIndex
      let color = 'text-gray-500'
      if (change > 0) {
        color = 'text-green-500'
      }
      if (change < 0) {
        color = 'text-red-500'
      }

      return (
        <span className={`font-bold ${color}`}>
          {change > 0 && <>↑</>}
          {change < 0 && <>↓</>}
          {Math.abs(oldIndex - newIndex)}
        </span>
      )
    },
    [rtrbTurn, realTimeRanking, tableTeams]
  )

  const hasGuildData = useMemo(() => tableTeams.some((team) => team.guild), [tableTeams])

  const formatScoreTableListContent = useCallback(
    (v, index) => {
      const userNames = [v.name_user1, v.name_user2, v.name_user3, v.name_user4].filter(Boolean)

      return {
        key: v.id,
        rank: (
          <div className="flex items-center">
            <div>第</div>
            <div className="w-10 text-center font-bold text-red-600">{match?.match_type === 'duplicated' ? index + 1 : v?.rank_index || index + 1}</div>
            <div>名</div>
            {v.data?.give_up && <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-solid border-red-500 text-red-500">弃</span>}
          </div>
        ),
        players: (
          <div className="flex flex-1 items-center gap-4 truncate font-bold">
            {userNames.map((name, idx) => (
              <div key={+idx} className="min-w-[80px] truncate">
                {name}
              </div>
            ))}
          </div>
        ),
        team: <div className="truncate">{v.name}</div>,
        scores: scores.map((s) => ({
          [s.data]: JSON.stringify(v[s.data]),
        })),
        penalty_score: v.penalty_score,
        rank_change: calculateRankChange(v.id),
        guild: <div className="truncate">{v.guild}</div>,
      }
    },
    [match?.match_type, scores, calculateRankChange]
  )

  const columns = useMemo(() => {
    const baseColumns: ColumnsType<any> = [
      {
        title: '名次',
        dataIndex: 'rank',
        key: 'rank',
        width: 100,
        fixed: 'left',
      },
      {
        title: '队伍',
        dataIndex: 'team',
        key: 'team',
        width: 100,
      },
      {
        title: '选手',
        dataIndex: 'players',
        key: 'players',
        width: 400,
      },
      ...scores.map((s, index) => {
        return {
          title: s.title,
          dataIndex: s.data,
          key: s.data,
          width: 150,
          render: (__, record) => record.scores[index][s.data],
        }
      }),
    ]

    if (match.match_type === 'spin') {
      baseColumns.push({
        title: '补分/罚分',
        dataIndex: 'penalty_score',
        key: 'penalty_score',
        align: 'center',
        width: 150,
      })
    }

    if (hasGuildData) {
      baseColumns.push({
        title: '所属团体',
        dataIndex: 'guild',
        key: 'guild',
        fixed: 'right',
      })
    }

    if (window?.innerWidth > 500) {
      baseColumns.push({
        title: '',
        dataIndex: '',
        key: 'hidden',
        fixed: 'right',
        width: 0,
      })
    }

    return baseColumns
  }, [scores, match.match_type, hasGuildData])

  // 表格内容
  const tableContent = useMemo(() => {
    const snTeams = tableTeams.filter((t) => t.direction === 'south_north')
    const ewTeams = tableTeams.filter((t) => t.direction === 'east_west')

    const data =
      match?.match_type === 'duplicated'
        ? [
            {
              title: '东西方',
              teams: ewTeams,
            },
            {
              title: '南北方',
              teams: snTeams,
            },
          ]
        : [
            {
              title: 'All Teams',
              teams: tableTeams,
            },
          ]

    return (
      <div className="flex flex-col gap-4">
        {data.map((group, i) =>
          group.teams.length === 0 ? (
            <Card key={+i}>
              <Empty />
            </Card>
          ) : (
            <ColumnResizeTable key={+i} columns={columns} dataSource={group.teams.map((v, index) => formatScoreTableListContent(v, index))} />
          )
        )}
      </div>
    )
  }, [tableTeams, match?.match_type, columns, formatScoreTableListContent])

  return (
    <div className="h-full w-full">
      {wechatShare}
      {showTurnSetter && (
        <div className="sticky top-6 z-40 mb-6 w-full px-2 lg:px-6">
          <div className="flex w-full flex-nowrap items-center justify-between gap-4 overflow-auto rounded-lg bg-stone-800 px-4 py-1 text-2xl font-bold text-white">
            <div className="flex items-center gap-4">
              <div className="flex w-max items-center text-2xl font-bold text-white">成绩排名</div>
              <TurnSetter defaultTurn={turn} setTurn={setTurn} matchTurn={match?.turn ?? 3} firstTurnName="总分" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {match.show_team_scores_via_turn && (
                <Button type="link" href={`/match/${match.id}/teams_score_via_turns`} className="mr-2 bg-white">
                  自定义多轮算分
                </Button>
              )}
              {match?.group_by_gender && (
                <Select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e)}
                  options={['全部', '男子组', '女子组', '混合组'].map((i) => ({ value: i, label: i }))}
                  className="w-40"
                />
              )}
              <Button
                type="primary"
                onClick={() => {
                  window.open(`/match/${match.id}/score_download`)
                }}
              >
                下载成绩单
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="px-2 py-6 lg:p-6">{tableContent}</div>
    </div>
  )
}

TeamsScore.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

export default TeamsScore
